import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Floating "back to top" button. The app scrolls inside the
 * <main class="overflow-y-auto"> container (not the window), so both the
 * scroll listener and the scroll-to-top target that container.
 */
export default function BackToTop() {
  const { t } = useTranslation(['common']);
  const [isVisible, setIsVisible] = useState(false);
  const scrollContainerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const container = document.querySelector('main');
    if (!container) return;
    scrollContainerRef.current = container;

    const toggleVisibility = () => {
      // Show button when the container is scrolled down 300px
      setIsVisible(container.scrollTop > 300);
    };

    toggleVisibility();
    container.addEventListener('scroll', toggleVisibility, { passive: true });

    return () => {
      container.removeEventListener('scroll', toggleVisibility);
    };
  }, []);

  const scrollToTop = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <button
      type="button"
      onClick={scrollToTop}
      className={`
        fixed bottom-6 right-6 z-50
        bg-blue-600 hover:bg-blue-700
        text-white rounded-full p-3
        shadow-lg transition-all duration-300
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}
      `}
      aria-label={t('common:backToTop')}
    >
      <svg
        className="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 10l7-7m0 0l7 7m-7-7v18"
        />
      </svg>
    </button>
  );
}
